import './insured-entity-grid.scss';

import { Box, Typography } from '@mui/material';
import {
  ConvergeTooltip,
  PaperLayout,
  CustomizedTable,
  createTextColumn,
  TableColumn,
} from '../../../presentation';
import {
  EntitySortKey,
  EntityStatus,
  EntitySubmissionType,
  useGetEntities,
} from '@converge/graphql';
import { InsuredEntityFieldLabelType, SortOrder } from '@converge/data';

import { ConvergeNavigationUrls } from '../../../contexts';
import { Link } from 'react-router-dom';
// import { convertInputToFormattedAmountUSD } from '@converge/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InsuredFilter from './insured-filter';
import { CheckedState } from '../../../presentation/components/table-filter';

const TruncatedCell = ({ text }: { text: string }) => (
  <ConvergeTooltip title={text}>
    <div
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'Red Hat Text',
      }}
    >
      {text}
    </div>
  </ConvergeTooltip>
);

export interface InsuredEntityDetails {
  uuid: string;
  referenceId: string;
  legalName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  createdAt: string;
  updatedAt: string;
  // revenue: number;
  naicsCode: string | number;
  documentsLink: string;
  industrySector: string;
  status: string;
  data: object;
}

interface InsuredRowData {
  uuid: string;
  referenceId: string;
  legalName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  // revenue: number;
  naicsCode: string | number;
  industrySector: string;
  status: string;
  [key: string]: unknown;
}

const STORAGE_KEY = 'insured-entities-sort';
const PAGE_SIZE_KEY = 'insuredPageSize';
const PAGE_NUMBER_KEY = 'insuredPageNumber';

const DEFAULT_INSURED_SORT = {
  field: EntitySortKey.CreatedAt,
  order: SortOrder.Desc,
};

const getStoredSort = (): { field: string; order: string } => {
  const DEFAULT_SORT = DEFAULT_INSURED_SORT;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Only restore if it's a visible/sortable field; fall back to default otherwise
      const visibleFields = [
        EntitySortKey.ReferenceId,
        EntitySortKey.PrimaryLegalName,
        EntitySortKey.Street1,
        // EntitySortKey.Revenue,
        EntitySortKey.NaicsCode,
        EntitySortKey.Status,
      ];
      if (visibleFields.includes(parsed.field)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored insured sort:', error);
  }
  return DEFAULT_SORT;
};

const setStoredSort = (sort: { field: string; order: string }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
  } catch (error) {
    console.warn('Failed to store insured sort:', error);
  }
};

export const InsuredEntityGrid = () => {
  const localStoragePageSize = parseInt(
    localStorage.getItem(PAGE_SIZE_KEY) ?? '0'
  );
  const localStoragePageNumber = parseInt(
    localStorage.getItem(PAGE_NUMBER_KEY) ?? '0'
  );

  const [filterOpen] = useState<boolean>(false);

  const storedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem('insuredFilters') || 'null');
    } catch {
      return null;
    }
  })();
  const storedFilterLabels = (() => {
    try {
      return JSON.parse(localStorage.getItem('insuredFilterLabels') || 'null');
    } catch {
      return null;
    }
  })();

  const emptyFilters: CheckedState = {
    insuredName: [],
    industrySector: [],
    status: [],
    // revenueFrom: [],
    // revenueTo: [],
  };

  const [currentFilters, setCurrentFilters] = useState<CheckedState>(
    storedFilters || emptyFilters
  );
  const [currentFilterLabels, setCurrentFilterLabels] = useState<CheckedState>(
    storedFilterLabels || emptyFilters
  );
  const [filterLabels, setFilterLabels] = useState<CheckedState>(
    storedFilterLabels || emptyFilters
  );

  const [currentPage, setCurrentPage] = useState<number>(
    localStoragePageNumber || 0
  );
  const [pageSize, setPageSize] = useState<number>(localStoragePageSize || 10);
  const [insuredEntitiesSort, setInsuredEntitiesSort] = useState<{
    field: string;
    order: string;
  }>(getStoredSort);

  // const revenueFrom = currentFilters['revenueFrom']?.[0];
  // const revenueTo = currentFilters['revenueTo']?.[0];
  // const insuredRevenueRange =
  //   revenueFrom || revenueTo
  //     ? {
  //         min: revenueFrom ? parseFloat(revenueFrom) : 0,
  //         max: revenueTo ? parseFloat(revenueTo) : Number.MAX_SAFE_INTEGER,
  //       }
  //     : undefined;

  const { loading, data, entities } = useGetEntities({
    pageNumber: currentPage,
    pageSize: pageSize,
    entityType: EntitySubmissionType.Insured,
    submissionFinalized: true,
    sort: insuredEntitiesSort,
    primaryIndustrySectorUuids: currentFilters['industrySector']?.length
      ? currentFilters['industrySector']
      : undefined,
    insuredNameUuids: currentFilters['insuredName']?.length
      ? currentFilters['insuredName']
      : undefined,
    statuses: currentFilters['status']?.length
      ? (currentFilters['status'] as EntityStatus[])
      : undefined,
    // insuredRevenueRange,
  });

  const { t } = useTranslation();

  const hasActiveFilters = Object.values(currentFilters).some(
    (arr) => arr.length > 0
  );

  // Map column id -> API sort key (InsuredEntityFieldLabelType values)
  const insuredSortKeys: Record<string, string> = {
    referenceId: EntitySortKey.ReferenceId,
    legalName: EntitySortKey.PrimaryLegalName,
    address1: EntitySortKey.Street1,
    // revenue: EntitySortKey.Revenue,
    naicsCode: EntitySortKey.NaicsCode,
    status: EntitySortKey.Status,
  };

  // Two-state sort toggle: asc -> desc -> asc -> ...
  // null (third-state reset from CustomizedTable) is treated as asc to keep the cycle going.
  const handleSortChange = (
    sortField: string,
    sortDirection: 'asc' | 'desc' | null
  ) => {
    const direction = sortDirection ?? 'asc';

    const sortConfig = {
      field:
        insuredSortKeys[sortField] || InsuredEntityFieldLabelType.ReferenceId,
      order: direction === 'desc' ? SortOrder.Desc : SortOrder.Asc,
    };

    setStoredSort(sortConfig);
    setInsuredEntitiesSort(sortConfig);
    setCurrentPage(0);
    localStorage.setItem(PAGE_NUMBER_KEY, '0');
  };

  // Map API sort key (InsuredEntityFieldLabelType) back to column id.
  const reverseFieldMap: Record<string, keyof InsuredRowData> = {
    [EntitySortKey.ReferenceId]: 'referenceId',
    [EntitySortKey.PrimaryLegalName]: 'legalName',
    [EntitySortKey.Street1]: 'address1',
    // [EntitySortKey.Revenue]: 'revenue',
    [EntitySortKey.NaicsCode]: 'naicsCode',
    [EntitySortKey.Status]: 'status',
  };

  const columns: TableColumn<InsuredRowData>[] = [
    createTextColumn<InsuredRowData>(
      'referenceId',
      InsuredEntityFieldLabelType.ReferenceId,
      {
        sortable: true,
        flex: 1.5,
        render: (value, row) => (
          <Link
            className="reference-id"
            to={ConvergeNavigationUrls.insuredEntityDetail({
              insuredEntityUuid: row.uuid,
            })}
          >
            {String(value)}
          </Link>
        ),
      }
    ),
    createTextColumn<InsuredRowData>(
      'legalName',
      InsuredEntityFieldLabelType.LegalName,
      {
        sortable: true,
        flex: 1.8,
        render: (value) => <TruncatedCell text={String(value ?? '—')} />,
      }
    ),
    createTextColumn<InsuredRowData>(
      'address1',
      InsuredEntityFieldLabelType.Address,
      {
        sortable: true,
        flex: 3.0,
        render: (value, row) => (
          <Typography className="table-cell">
            {row.address1}, {row.address2 ? `${row.address2},` : ''} {row.city},{' '}
            {row.state}, {row.postalCode}, {row.country}
          </Typography>
        ),
      }
    ),
    // createTextColumn<InsuredRowData>(
    //   'revenue',
    //   InsuredEntityFieldLabelType.Revenue,
    //   {
    //     sortable: true,
    //     flex: 1,
    //     render: (value) => (
    //       <Typography className="table-cell">
    //         {convertInputToFormattedAmountUSD(value as number)}
    //       </Typography>
    //     ),
    //   }
    // ),
    createTextColumn<InsuredRowData>(
      'naicsCode',
      InsuredEntityFieldLabelType.NaicsCode,
      {
        sortable: true,
        flex: 3,
        render: (value, row) => (
          <TruncatedCell text={row.industrySector || ''} />
        ),
      }
    ),
    createTextColumn<InsuredRowData>(
      'status',
      InsuredEntityFieldLabelType.Status,
      {
        sortable: true,
        flex: 1,
        align: 'center',
        render: (value) => {
          const statusValue = String(value ?? '');
          let color = '#D32F2F';
          let backgroundColor = '#FDEDED';
          let displayValue = statusValue;

          if (statusValue === 'ACTIVE') {
            color = '#2E7D32';
            backgroundColor = '#EDF7ED';
            displayValue = 'Active';
          } else if (statusValue === 'OUT_OF_BUSINESS') {
            color = '#D32F2F';
            backgroundColor = '#FDEDED';
            displayValue = 'Inactive';
          } else {
            color = '#0288D1';
            backgroundColor = '#E5F6FD';
            displayValue = 'Inactive';
          }

          return (
            <div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '3px 24px',
                  borderRadius: '16px',
                  border: `1px solid ${color}`,
                  backgroundColor: `${backgroundColor}`,
                  fontSize: '12px',
                  fontWeight: '500',
                  color: color,
                  width: '65px',
                  justifyContent: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {displayValue || '—'}
              </span>
            </div>
          );
        },
      }
    ),
  ];

  return (
    <>
      <Box className="entity-grid-header-title"></Box>
      <PaperLayout className="entity-grid-layout entity-grid-header-title">
        <Box className="insured-entity-list" sx={{ width: '100%' }}>
          <InsuredFilter
            open={filterOpen}
            currentFilters={currentFilters}
            setCurrentFilters={setCurrentFilters}
            currentFilterLabels={currentFilterLabels}
            setCurrentFilterLabels={setCurrentFilterLabels}
            filterLabels={filterLabels}
            setFilterLabels={setFilterLabels}
            resetPageNumber={() => {
              setCurrentPage(0);
              localStorage.setItem(PAGE_NUMBER_KEY, '0');
            }}
          />
          <CustomizedTable<InsuredRowData>
            data={(entities as InsuredRowData[]) || []}
            maxHeight="calc(80vh - 18vh)"
            columns={columns}
            enableSorting={true}
            enablePagination={true}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            defaultRowsPerPage={pageSize}
            loading={loading}
            onPageChange={(pageNumber) => {
              setCurrentPage(pageNumber);
              localStorage.setItem(PAGE_NUMBER_KEY, pageNumber.toString());
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              localStorage.setItem(PAGE_SIZE_KEY, newPageSize.toString());
            }}
            onSortChange={(columnId, sortOrder) =>
              handleSortChange(String(columnId), sortOrder)
            }
            onSortReset={() => {
              setStoredSort(DEFAULT_INSURED_SORT);
              setInsuredEntitiesSort(DEFAULT_INSURED_SORT);
              setCurrentPage(0);
              localStorage.setItem(PAGE_NUMBER_KEY, '0');
            }}
            currentSortColumn={reverseFieldMap[insuredEntitiesSort.field]}
            currentSortOrder={
              reverseFieldMap[insuredEntitiesSort.field] !== undefined
                ? insuredEntitiesSort.order === SortOrder.Desc
                  ? 'desc'
                  : 'asc'
                : undefined
            }
            currentPage={currentPage}
            totalItems={data?.entities?.pageInfo.totalResults || 0}
            message={t(
              hasActiveFilters
                ? 'entities.insureds.grid.emptyMessageFiltered'
                : 'entities.insureds.grid.emptyMessage'
            )}
          />
        </Box>
      </PaperLayout>
    </>
  );
};
